import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    ArrowLeft,
    Users,
    Check,
    Calendar,
    Clock,
    Repeat,
    Loader2,
    ClipboardCheck,
    Send,
} from 'lucide-react';

interface Student {
    id: string;
    full_name: string;
    email: string;
    avatar_url: string | null;
    pairing: {
        id: string;
        is_premium_seat: boolean;
    };
}

interface Workout {
    id: string;
    name: string;
    description: string | null;
    duration_minutes: number | null;
    difficulty: string;
}

export function PrescribePage() {
    const { id: workoutId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { tenant, profile } = useAuth();
    const queryClient = useQueryClient();

    const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [frequency, setFrequency] = useState(3);
    const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 3, 5]); // Mon, Wed, Fri
    const [notes, setNotes] = useState('');

    // Fetch workout
    const { data: workout, isLoading: isLoadingWorkout } = useQuery({
        queryKey: ['workout', workoutId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('workouts')
                .select('id, name, description, duration_minutes, difficulty')
                .eq('id', workoutId)
                .single();

            if (error) throw error;
            return data as Workout;
        },
        enabled: !!workoutId,
    });

    // Fetch students
    const { data: students, isLoading: isLoadingStudents } = useQuery({
        queryKey: ['students-prescribe', tenant?.id],
        queryFn: async () => {
            if (!tenant?.id) return [];

            const { data, error } = await supabase
                .from('student_pairings')
                .select(`
                    id,
                    is_premium_seat,
                    student:student_id (
                        id,
                        full_name,
                        email,
                        avatar_url
                    )
                `)
                .eq('tenant_id', tenant.id)
                .eq('status', 'active');

            if (error) throw error;

            return (data || []).map((p) => ({
                ...(p.student as unknown as Record<string, unknown>),
                pairing: {
                    id: p.id,
                    is_premium_seat: p.is_premium_seat,
                },
            })) as Student[];
        },
        enabled: !!tenant?.id,
    });

    // Create prescriptions
    const prescribeMutation = useMutation({
        mutationFn: async () => {
            if (!tenant?.id || !profile?.id || !workoutId) return;

            const prescriptions = selectedStudents.map((studentId) => ({
                tenant_id: tenant.id,
                workout_id: workoutId,
                student_id: studentId,
                prescribed_by: profile.id,
                start_date: startDate,
                end_date: endDate || null,
                frequency_per_week: frequency,
                days_of_week: daysOfWeek,
                notes: notes || null,
                is_active: true,
            }));

            const { error } = await supabase
                .from('workout_prescriptions')
                .insert(prescriptions);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workouts'] });
            navigate('/workouts');
        },
    });

    const toggleStudent = (studentId: string) => {
        setSelectedStudents((prev) =>
            prev.includes(studentId)
                ? prev.filter((id) => id !== studentId)
                : [...prev, studentId]
        );
    };

    const toggleAllStudents = () => {
        if (selectedStudents.length === students?.length) {
            setSelectedStudents([]);
        } else {
            setSelectedStudents(students?.map((s) => s.id) || []);
        }
    };

    const toggleDayOfWeek = (day: number) => {
        setDaysOfWeek((prev) =>
            prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
        );
    };

    const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    const handlePrescribe = () => {
        if (selectedStudents.length === 0) {
            alert('Selecione pelo menos um aluno');
            return;
        }
        prescribeMutation.mutate();
    };

    if (isLoadingWorkout || isLoadingStudents) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1">
                    <h1 className="section-title">Prescrever Treino</h1>
                    <p className="section-subtitle">
                        Atribua o treino aos seus alunos
                    </p>
                </div>
            </div>

            {/* Workout Info */}
            <div className="card bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-600 flex items-center justify-center">
                        <ClipboardCheck className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-bold text-gray-900">{workout?.name}</h2>
                        {workout?.description && (
                            <p className="text-sm text-gray-600">{workout.description}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                        {workout?.duration_minutes && (
                            <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                <span>{workout.duration_minutes} min</span>
                            </div>
                        )}
                        <span className="badge badge-primary">{workout?.difficulty}</span>
                    </div>
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Select Students */}
                <div className="card">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-semibold flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Selecionar Alunos
                        </h2>
                        <button
                            onClick={toggleAllStudents}
                            className="text-sm text-primary-600 hover:text-primary-700"
                        >
                            {selectedStudents.length === students?.length
                                ? 'Desmarcar todos'
                                : 'Selecionar todos'}
                        </button>
                    </div>

                    {students && students.length > 0 ? (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                            {students.map((student) => (
                                <label
                                    key={student.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedStudents.includes(student.id)
                                        ? 'border-primary-500 bg-primary-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedStudents.includes(student.id)}
                                        onChange={() => toggleStudent(student.id)}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold">
                                        {student.full_name?.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900">
                                            {student.full_name}
                                        </p>
                                        <p className="text-sm text-gray-500">{student.email}</p>
                                    </div>
                                    {selectedStudents.includes(student.id) && (
                                        <Check className="w-5 h-5 text-primary-600" />
                                    )}
                                </label>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <Users className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                            <p>Nenhum aluno cadastrado</p>
                        </div>
                    )}

                    <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-gray-500">
                            <strong>{selectedStudents.length}</strong> aluno(s) selecionado(s)
                        </p>
                    </div>
                </div>

                {/* Schedule */}
                <div className="card">
                    <h2 className="font-semibold flex items-center gap-2 mb-4">
                        <Calendar className="w-5 h-5" />
                        Configurar Período
                    </h2>

                    <div className="space-y-4">
                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="label">Data de Início</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="input"
                                />
                            </div>
                            <div>
                                <label className="label">Data de Término (opcional)</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="input"
                                    min={startDate}
                                />
                            </div>
                        </div>

                        {/* Frequency */}
                        <div>
                            <label className="label flex items-center gap-2">
                                <Repeat className="w-4 h-4" />
                                Frequência Semanal
                            </label>
                            <div className="flex items-center gap-4">
                                <input
                                    type="range"
                                    min="1"
                                    max="7"
                                    value={frequency}
                                    onChange={(e) => setFrequency(parseInt(e.target.value))}
                                    className="flex-1"
                                />
                                <span className="font-bold text-primary-600 w-20 text-center">
                                    {frequency}x / semana
                                </span>
                            </div>
                        </div>

                        {/* Days of Week */}
                        <div>
                            <label className="label">Dias da Semana</label>
                            <div className="flex gap-2">
                                {DAYS.map((day, index) => (
                                    <button
                                        key={day}
                                        type="button"
                                        onClick={() => toggleDayOfWeek(index)}
                                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${daysOfWeek.includes(index)
                                            ? 'bg-primary-600 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {day}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="label">Observações (opcional)</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Instruções especiais para este treino..."
                                className="input"
                                rows={3}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 justify-end">
                <button onClick={() => navigate(-1)} className="btn-secondary">
                    Cancelar
                </button>
                <button
                    onClick={handlePrescribe}
                    disabled={selectedStudents.length === 0 || prescribeMutation.isPending}
                    className="btn-primary"
                >
                    {prescribeMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                        <Send className="w-4 h-4 mr-2" />
                    )}
                    Prescrever para {selectedStudents.length} aluno(s)
                </button>
            </div>
        </div>
    );
}
